import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../api";
import { Capacitor } from "@capacitor/core";
import { supabase } from "../supabaseClient";
import { describeNativeScannerError, isNativeScannerAvailable, scanPassQrNative } from "../features/vendorScanner/nativeScan";
import { formatDateTime, makeRequestId, parsePassInput, toQuery } from "../features/vendorScanner/scannerUtils";
import { clearPendingScans, loadPendingScans, pushPendingScan, removePendingScan } from "../features/vendorScanner/offlineStore";

const SESSION_VENDOR_KEY = "bbp_vendor_api_key";

function statusColor(status) {
    switch (status) {
        case "VALID":
        case "READY_TO_ACTIVATE":
        case "ACTIVE":
        case "active":
        case "success":
        case "SUCCESS":
            return "#2ecc71";
        case "ALREADY_USED_FOR_ACTIVITY":
        case "duplicate":
            return "#f39c12";
        case "OUTSIDE_TRIP_WINDOW":
        case "MISSING_TRIP_WINDOW":
        case "NOT_STARTED":
        case "EXPIRED":
        case "expired":
            return "#f1c40f";
        case "PENDING_SYNC":
        case "pending_sync":
            return "#8e44ad";
        default:
            return "#e74c3c";
    }
}

function normalizePendingRow(row) {
    return {
        id: row.request_id,
        created_at: row.created_at,
        event_type: row.event_type,
        result: row.result,
        status: row.status,
        message: row.message,
        request_id: row.request_id,
        scan_source: row.scan_source,
        device_id: row.device_id,
        is_offline: true,
        reservation_ref: row.reservation_ref,
        email: row.email,
        activity: row.activity,
        pass: row.pass,
        user: null,
        snapshot: row,
        pending_local: true,
    };
}

function firstScannerChunk(value) {
    return (
        String(value || "")
            .split(/[\r\n]+/)
            .map((part) => part.trim())
            .filter(Boolean)[0] || ""
    );
}

function normalizeSinglePassInput(value, previousValue = "") {
    const raw = String(value || "");
    const previous = String(previousValue || "").trim();

    // Final/process-time normalization. This is used when the scanner submits
    // with Enter, the camera returns a complete payload, or a user clicks Verify.
    // It should collapse complete JSON/URL/text payloads to one UUID only.
    const previousPass = parsePassInput(previous);
    if (previousPass && raw.startsWith(previousPass) && raw.length > previousPass.length) {
        const freshScanChunk = raw.slice(previousPass.length).trim();
        const parsedFreshScan = parsePassInput(freshScanChunk);
        if (parsedFreshScan) return parsedFreshScan;

        // If the only new characters are a JSON tail from the same scan, keep the
        // existing UUID instead of letting characters like "} stick to the field.
        if (/^["'`}\]]{1,8}$/.test(freshScanChunk)) return previousPass;
        return firstScannerChunk(freshScanChunk).slice(0, 2000);
    }

    const parsed = parsePassInput(raw);
    if (parsed) return parsed;

    return firstScannerChunk(raw).slice(0, 2000);
}

function normalizePassTypingInput(value, previousValue = "") {
    const raw = String(value || "");
    const previous = String(previousValue || "").trim();
    const previousPass = parsePassInput(previous);

    // Do NOT collapse JSON to a UUID while a hardware scanner is still typing.
    // If we collapse early, the scanner continues typing the remaining JSON tail
    // and the field ends up like: <uuid>"}.
    if (previousPass && raw.startsWith(previousPass) && raw.length > previousPass.length) {
        const freshScanChunk = raw.slice(previousPass.length).trimStart();
        const parsedFreshScan = parsePassInput(freshScanChunk);
        if (parsedFreshScan) return parsedFreshScan;

        // A tiny JSON tail means the previous scan finished after we had already
        // collapsed the UUID. Ignore the tail instead of showing "}.
        if (/^["'`}\]]{1,8}$/.test(freshScanChunk.trim())) return previousPass;

        return firstScannerChunk(freshScanChunk).slice(0, 2000);
    }

    return firstScannerChunk(raw).slice(0, 2000);
}


const EXPORT_LIMIT = 5000;

function scanValue(row) {
    return String(row?.result || row?.status || "").trim().toLowerCase();
}

function isGoodScan(row) {
    const value = scanValue(row);
    return value === "success" || value === "valid" || value === "ready_to_activate";
}

function isPendingScan(row) {
    const value = scanValue(row);
    return row?.pending_local || row?.status === "PENDING_SYNC" || value === "pending_sync" || value === "pending";
}

function isDuplicateScan(row) {
    const value = scanValue(row);
    return value === "duplicate" || row?.status === "ALREADY_USED_FOR_ACTIVITY";
}

function isBadScan(row) {
    return !isGoodScan(row) && !isPendingScan(row);
}

function buildHistorySummary(rows = []) {
    const safeRows = Array.isArray(rows) ? rows : [];
    return {
        total: safeRows.length,
        good: safeRows.filter(isGoodScan).length,
        bad: safeRows.filter(isBadScan).length,
        duplicate: safeRows.filter(isDuplicateScan).length,
        pending: safeRows.filter(isPendingScan).length,
        verify: safeRows.filter((row) => row.event_type === "verify").length,
        redeem: safeRows.filter((row) => row.event_type === "redeem").length,
        offline: safeRows.filter((row) => row.is_offline || row.pending_local).length,
        activated: safeRows.filter((row) => row.snapshot?.activated_now).length,
    };
}

function getRowPass(row) {
    return row?.pass || row?.snapshot?.pass || {};
}

function getRowUser(row) {
    return row?.user || row?.snapshot?.user || {};
}

function getRowActivity(row) {
    return row?.activity || row?.snapshot?.activity || {};
}

function safeText(value) {
    if (value === undefined || value === null) return "";
    return String(value).replace(/\s+/g, " ").trim();
}

function csvCell(value) {
    const text = safeText(value);
    return `"${text.replace(/"/g, '""')}"`;
}

function htmlCell(value) {
    return safeText(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function slugify(value) {
    return safeText(value || "vendor")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "vendor";
}

function makeExportRows(rows = []) {
    return rows.map((row) => {
        const snapshot = row.snapshot || {};
        const pass = getRowPass(row);
        const user = getRowUser(row);
        const activity = getRowActivity(row);
        const createdAt = row.created_at || snapshot.timestamp || "";

        return {
            "Timestamp": createdAt ? formatDateTime(createdAt) : "",
            "Event Type": row.event_type || "",
            "Result": row.result || snapshot.result || "",
            "Status": row.status || snapshot.status || "",
            "Good Scan": isGoodScan(row) ? "Yes" : "No",
            "Bad Scan": isBadScan(row) ? "Yes" : "No",
            "Pending Local": isPendingScan(row) ? "Yes" : "No",
            "Activity Name": activity.name || "",
            "Activity ID": activity.id || row.activity_id || "",
            "Reservation Required": activity.requires_reservation === true ? "Yes" : activity.requires_reservation === false ? "No" : "",
            "Pass ID": pass.id || snapshot.pass_id || row.pass_uuid || "",
            "Pass Type": pass.pass_type || pass.name || snapshot.pass_name || "",
            "Pass Status": pass.status || "",
            "Duration Days": pass.duration_days ?? snapshot.duration ?? "",
            "Pass Price": pass.price ?? "",
            "Holder Type": pass.holder_type || "",
            "Holder Name": pass.holder_name || "",
            "Holder Age": pass.holder_age ?? "",
            "Traveler Email": user.email || row.email || snapshot.email || "",
            "Username": user.username || "",
            "User ID": user.id || "",
            "Trip Start": pass.planned_start_date || snapshot.planned_from || "",
            "Trip End": pass.planned_end_date || snapshot.planned_to || "",
            "Active Start": pass.start_date || snapshot.valid_from || "",
            "Active End": pass.end_date || snapshot.valid_to || "",
            "Activated Now": snapshot.activated_now === true ? "Yes" : snapshot.activated_now === false ? "No" : "",
            "Activated At": pass.activated_at || "",
            "Purchase Order ID": pass.purchase_order_id || "",
            "Invite ID": pass.source_invite_id || "",
            "Redemption ID": snapshot.redemption_id || "",
            "Redemptions Used": snapshot.redemptions_used ?? "",
            "Redemptions Allowed": snapshot.redemptions_allowed ?? activity.max_redemptions_per_pass ?? "",
            "Redemption Mode": snapshot.redemption_mode || activity.redemption_mode || "",
            "Reservation Ref": row.reservation_ref || snapshot.reservation_ref || "",
            "Scan Source": row.scan_source || snapshot.scan_source || "",
            "Offline": row.is_offline || row.pending_local ? "Yes" : "No",
            "Request ID": row.request_id || snapshot.request_id || "",
            "Device ID": row.device_id || snapshot.device_id || "",
            "Message": row.message || snapshot.message || "",
        };
    });
}

function summaryPairs(summary, vendor, filters) {
    return [
        ["Vendor", vendor?.name || ""],
        ["Location", vendor?.location || ""],
        ["Generated", formatDateTime(new Date().toISOString())],
        ["Filters", [filters?.event_type, filters?.result, filters?.activity_id].filter(Boolean).join(" | ") || "All scan events"],
        ["Total Scans", summary.total],
        ["Good Scans", summary.good],
        ["Bad Scans", summary.bad],
        ["Duplicate Scans", summary.duplicate],
        ["Verify Events", summary.verify],
        ["Redemption Events", summary.redeem],
        ["Pending Local", summary.pending],
        ["Offline", summary.offline],
        ["Activated Passes", summary.activated],
    ];
}

function buildCsvReport(rows, summary, vendor, filters) {
    const exportRows = makeExportRows(rows);
    const headers = Object.keys(exportRows[0] || { "No Rows": "" });
    const lines = [
        ["Beach Bum Pass Vendor Scan Report"],
        ...summaryPairs(summary, vendor, filters),
        [],
        headers,
        ...exportRows.map((row) => headers.map((header) => row[header])),
    ];

    return `\uFEFF${lines.map((line) => line.map(csvCell).join(",")).join("\n")}`;
}

function buildSpreadsheetReport(rows, summary, vendor, filters) {
    const exportRows = makeExportRows(rows);
    const headers = Object.keys(exportRows[0] || { "No Rows": "" });
    const summaryHtml = summaryPairs(summary, vendor, filters)
        .map(([label, value]) => `<tr><th>${htmlCell(label)}</th><td>${htmlCell(value)}</td></tr>`)
        .join("");
    const bodyHtml = exportRows
        .map((row) => `<tr>${headers.map((header) => `<td>${htmlCell(row[header])}</td>`).join("")}</tr>`)
        .join("");

    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<style>
body { font-family: Arial, sans-serif; color: #043447; }
h1 { font-size: 22px; }
table { border-collapse: collapse; width: 100%; margin: 16px 0; }
th, td { border: 1px solid #b9dce4; padding: 8px; text-align: left; vertical-align: top; }
th { background: #e8f8fb; font-weight: 700; }
.summary th { width: 220px; }
</style>
</head>
<body>
<h1>Beach Bum Pass Vendor Scan Report</h1>
<table class="summary">${summaryHtml}</table>
<table>
<thead><tr>${headers.map((header) => `<th>${htmlCell(header)}</th>`).join("")}</tr></thead>
<tbody>${bodyHtml || `<tr><td colspan="${headers.length}">No rows</td></tr>`}</tbody>
</table>
</body>
</html>`;
}

function downloadTextFile(filename, content, type) {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 400);
}

function StatusPill({ status }) {
    return (
        <span className="vendor-status-pill" style={{ "--vendor-status": statusColor(status || "INVALID") }}>
            {status || "UNKNOWN"}
        </span>
    );
}

export default function VendorPortal() {
    const [session, setSession] = useState(null);
    const [vendor, setVendor] = useState(null);
    const [activities, setActivities] = useState([]);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [apiKey, setApiKey] = useState(() => sessionStorage.getItem(SESSION_VENDOR_KEY) || "");
    const passInputRef = useRef(null);
    const scanProcessingRef = useRef(false);
    const recentScanRef = useRef({ passKey: "", at: 0 });
    const historyRefreshTimerRef = useRef(null);
    const isNativeApp = Capacitor.isNativePlatform();

    const [passInput, setPassInput] = useState("");
    const [lastProcessedPassKey, setLastProcessedPassKey] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [selectedActivity, setSelectedActivity] = useState("");
    const [reservationRef, setReservationRef] = useState("");
    const [vendorNotes, setVendorNotes] = useState("");
    const [autoRedeem, setAutoRedeem] = useState(true);
    const [scanSource, setScanSource] = useState(() => (Capacitor.isNativePlatform() ? "camera" : "hardware_scanner"));

    const [verifyResult, setVerifyResult] = useState(null);
    const [redeemResult, setRedeemResult] = useState(null);
    const [history, setHistory] = useState([]);
    const [pendingScans, setPendingScans] = useState([]);
    const [historyFilters, setHistoryFilters] = useState({ event_type: "", result: "", activity_id: "", limit: 100 });
    const [activeTab, setActiveTab] = useState("scan");

    const [loading, setLoading] = useState(false);
    const [scanBusy, setScanBusy] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState("");
    const [error, setError] = useState("");
    const [status, setStatus] = useState("");

    useEffect(() => {
        let mounted = true;

        supabase.auth.getSession().then(({ data }) => {
            if (mounted) setSession(data?.session ?? null);
        });

        const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
            setSession(nextSession ?? null);
            if (!nextSession) {
                setVendor(null);
                setActivities([]);
                setHistory([]);
            }
        });

        return () => {
            mounted = false;
            sub?.subscription?.unsubscribe?.();
        };
    }, []);

    useEffect(() => {
        if (!vendor?.id) {
            setPendingScans([]);
            return;
        }
        setPendingScans(loadPendingScans(vendor.id));
    }, [vendor?.id]);

    useEffect(() => {
        return () => {
            if (historyRefreshTimerRef.current) {
                window.clearTimeout(historyRefreshTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const resume = async () => {
            if (!session || !apiKey || vendor) return;
            try {
                await unlockVendorPortal(apiKey, false);
            } catch {
                // handled inside unlockVendorPortal
            }
        };
        resume();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session]);

    const mergedHistory = useMemo(() => {
        const pending = pendingScans.map(normalizePendingRow);
        return [...pending, ...history];
    }, [history, pendingScans]);

    const selectedActivityDetails = useMemo(
        () => activities.find((a) => String(a.id) === String(selectedActivity)) || null,
        [activities, selectedActivity]
    );

    const historySummary = useMemo(() => buildHistorySummary(mergedHistory), [mergedHistory]);

    const focusPassField = () => {
        passInputRef.current?.focus?.();
    };

    const processScannedPass = async (rawValue, source = scanSource) => {
        const displayValue = normalizeSinglePassInput(rawValue, passInput);
        const parsedPass = parsePassInput(displayValue) || parsePassInput(rawValue);

        setPassInput(displayValue);
        setScanSource(source);

        if (!parsedPass) {
            setStatus("QR scanned, but the payload did not look like a valid Beach Bum Pass yet.");
            return;
        }

        const now = Date.now();
        const recent = recentScanRef.current || {};
        if (scanProcessingRef.current) {
            setStatus("Already processing a pass. Hold tight for the next scan.");
            setPassInput(parsedPass);
            return;
        }
        if (recent.passKey === parsedPass && now - recent.at < 1800) {
            setStatus("That pass was just scanned. Ready for the next one.");
            setPassInput("");
            window.setTimeout(() => passInputRef.current?.focus?.(), 80);
            return;
        }

        setLastProcessedPassKey(parsedPass);

        if (!selectedActivity) {
            setStatus("Pass scanned. Select an activity to continue.");
            return;
        }

        scanProcessingRef.current = true;
        recentScanRef.current = { passKey: parsedPass, at: now };

        try {
            await handleVerify(parsedPass, source);
        } finally {
            scanProcessingRef.current = false;
        }
    };

    const handleNativeScan = async () => {
        setError("");
        setStatus("");
        setVerifyResult(null);
        setRedeemResult(null);

        if (!isNativeScannerAvailable()) {
            setError("Camera scanning is only available in the installed mobile app.");
            return;
        }

        setScanBusy(true);
        try {
            const rawValue = await scanPassQrNative();
            if (!rawValue) {
                setStatus("Scan canceled.");
                return;
            }

            await processScannedPass(rawValue, "camera");
        } catch (err) {
            console.error("native scan error:", err);
            setError(describeNativeScannerError(err));
        } finally {
            setScanBusy(false);
        }
    };

    const handlePassFieldKeyDown = async (e) => {
        if (isNativeApp) return;
        if (e.key !== "Enter" || e.shiftKey) return;

        e.preventDefault();
        if (scanProcessingRef.current || loading) return;

        const rawValue = normalizeSinglePassInput(e.currentTarget.value, passInput);
        if (!rawValue?.trim()) return;

        await processScannedPass(rawValue, "hardware_scanner");
    };

    async function unlockVendorPortal(keyToUse, persist = true) {
        const cleanKey = String(keyToUse || "").trim();
        const res = await axios.post("/vendor/login", { api_key: cleanKey });
        setVendor(res.data.vendor);
        setActivities(res.data.activities || []);
        setSelectedActivity((current) => current || res.data.activities?.[0]?.id || "");
        setError("");
        setStatus(`Unlocked ${res.data.vendor.name}.`);
        if (persist) {
            sessionStorage.setItem(SESSION_VENDOR_KEY, cleanKey);
            setApiKey(cleanKey);
        }
        await fetchHistory(cleanKey, historyFilters);
        return res.data;
    }

    const handleVendorLogin = async (e) => {
        e?.preventDefault?.();
        setError("");
        setStatus("");

        if (!email.trim() || !password) {
            setError("Enter your vendor email and password.");
            return;
        }
        if (!apiKey.trim()) {
            setError("Enter your vendor API key.");
            return;
        }

        setLoading(true);
        try {
            const signIn = await supabase.auth.signInWithPassword({
                email: email.trim().toLowerCase(),
                password,
            });

            if (signIn.error) {
                setError(signIn.error.message || "Vendor login failed.");
                return;
            }

            await unlockVendorPortal(apiKey);
        } catch (err) {
            console.error("Vendor login error:", err);
            setError(err.response?.data?.error || err.message || "Vendor login failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleVendorLogout = async () => {
        sessionStorage.removeItem(SESSION_VENDOR_KEY);
        clearPendingScans(vendor?.id);
        setPendingScans([]);
        setVendor(null);
        setActivities([]);
        setVerifyResult(null);
        setRedeemResult(null);
        setHistory([]);
        setStatus("Signed out.");
        setError("");
        await supabase.auth.signOut();
    };

    const fetchHistory = async (overrideKey = apiKey, filters = historyFilters, options = {}) => {
        if (!vendor && !session) return;
        if (historyLoading && !options.force) return;

        setHistoryLoading(true);
        try {
            const query = toQuery(filters);
            const res = await axios.get(`/vendor/redemptions${query}`, {
                headers: { "x-vendor-key": overrideKey },
            });
            setHistory(res.data.events || []);
        } catch (err) {
            console.error("history load error:", err);
            if (err.response?.status === 429) {
                setStatus("Scan saved. History is cooling down for a moment so the portal does not spam the server.");
                return;
            }
            setError(err.response?.data?.error || "Failed to load vendor scan history.");
        } finally {
            setHistoryLoading(false);
        }
    };

    const scheduleHistoryRefresh = (delay = 1200) => {
        if (historyRefreshTimerRef.current) {
            window.clearTimeout(historyRefreshTimerRef.current);
        }

        historyRefreshTimerRef.current = window.setTimeout(() => {
            fetchHistory(apiKey, historyFilters);
        }, delay);
    };

    const handleExport = async (format = "csv") => {
        if (!vendor) return;

        setError("");
        setStatus("");
        setExportLoading(format);

        try {
            const exportFilters = { ...historyFilters, limit: EXPORT_LIMIT };
            const query = toQuery(exportFilters);
            const res = await axios.get(`/vendor/redemptions${query}`, {
                headers: { "x-vendor-key": apiKey },
            });

            const rows = [...pendingScans.map(normalizePendingRow), ...(res.data.events || [])];
            const summary = buildHistorySummary(rows);
            const dateStamp = new Date().toISOString().slice(0, 10);
            const baseName = `bbp-${slugify(vendor.name)}-scan-report-${dateStamp}`;

            if (!rows.length) {
                setStatus("No scan history is available to export yet.");
                return;
            }

            if (format === "spreadsheet") {
                downloadTextFile(
                    `${baseName}.xls`,
                    buildSpreadsheetReport(rows, summary, vendor, historyFilters),
                    "application/vnd.ms-excel;charset=utf-8"
                );
                setStatus(`Spreadsheet downloaded with ${rows.length} scan records.`);
                return;
            }

            downloadTextFile(
                `${baseName}.csv`,
                buildCsvReport(rows, summary, vendor, historyFilters),
                "text/csv;charset=utf-8"
            );
            setStatus(`CSV downloaded with ${rows.length} scan records.`);
        } catch (err) {
            console.error("export error:", err);
            setError(err.response?.data?.error || "Failed to export vendor scan history.");
        } finally {
            setExportLoading("");
        }
    };

    const recordPending = (eventType, parsedPassKey) => {
        if (!vendor?.id) return;
        const requestId = makeRequestId(eventType);
        const activity = activities.find((a) => String(a.id) === String(selectedActivity));
        const pending = {
            request_id: requestId,
            created_at: new Date().toISOString(),
            event_type: eventType,
            result: "pending_sync",
            status: "PENDING_SYNC",
            message: "Recorded locally while the network was unavailable. Sync this in the scanner app later.",
            scan_source: scanSource,
            device_id: typeof navigator !== "undefined" ? navigator.userAgent : null,
            email: customerEmail || null,
            reservation_ref: reservationRef || null,
            activity: activity
                ? { id: activity.id, name: activity.name, category: activity.category, location: activity.location }
                : { id: selectedActivity, name: null },
            pass: { id: parsedPassKey || parsePassInput(passInput) || null },
        };
        setPendingScans(pushPendingScan(vendor.id, pending));
        setStatus("Saved locally as a pending offline scan.");
    };

    const handleVerify = async (overridePassKey = "", overrideScanSource = "") => {
        setError("");
        setStatus("");
        setVerifyResult(null);
        setRedeemResult(null);

        const parsedPassKey = overridePassKey || parsePassInput(passInput);
        const effectiveScanSource = overrideScanSource || scanSource;
        if (!parsedPassKey) {
            setError("Enter or scan a valid pass QR / pass UUID.");
            return;
        }
        if (!selectedActivity) {
            setError("Select an activity first.");
            return;
        }

        setLoading(true);
        const requestId = makeRequestId("verify");
        try {
            const res = await axios.post(
                "/vendor/verify",
                {
                    pass_key: parsedPassKey,
                    email: customerEmail || undefined,
                    activity_id: selectedActivity,
                    request_id: requestId,
                    scan_source: effectiveScanSource,
                    device_id: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
                },
                { headers: { "x-vendor-key": apiKey } }
            );

            setVerifyResult(res.data);
            setStatus(res.data.message || "Verification complete.");

            if (res.data?.valid) {
                setLastProcessedPassKey(parsedPassKey);
                setPassInput("");
                window.setTimeout(() => passInputRef.current?.focus?.(), 80);
            }

            if (!autoRedeem) {
                scheduleHistoryRefresh();
            }

            if (autoRedeem && res.data?.valid) {
                await handleRedeem(parsedPassKey, effectiveScanSource);
            }
        } catch (err) {
            console.error("verify error:", err);
            if (!err.response) {
                recordPending("verify", parsedPassKey);
            } else {
                setError(err.response?.data?.error || "Verification failed.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRedeem = async (overridePassKey = "", overrideScanSource = "") => {
        setError("");
        setStatus("");
        setRedeemResult(null);

        const parsedPassKey = overridePassKey || parsePassInput(passInput) || lastProcessedPassKey;
        const effectiveScanSource = overrideScanSource || scanSource;
        if (!parsedPassKey) {
            setError("Enter or scan a valid pass QR / pass UUID.");
            return;
        }
        if (!selectedActivity) {
            setError("Select an activity first.");
            return;
        }

        setLoading(true);
        const requestId = makeRequestId("redeem");
        try {
            const res = await axios.post(
                "/vendor/redeem",
                {
                    pass_key: parsedPassKey,
                    email: customerEmail || undefined,
                    activity_id: selectedActivity,
                    reservation_ref: reservationRef || undefined,
                    vendor_notes: vendorNotes || undefined,
                    request_id: requestId,
                    scan_source: effectiveScanSource,
                    device_id: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
                },
                { headers: { "x-vendor-key": apiKey } }
            );

            setRedeemResult(res.data);
            setStatus(res.data.message || "Redemption complete.");
            setLastProcessedPassKey(parsedPassKey);
            setPassInput("");
            window.setTimeout(() => passInputRef.current?.focus?.(), 80);
            scheduleHistoryRefresh();
            if (vendor?.id) {
                setPendingScans(removePendingScan(vendor.id, requestId));
            }
        } catch (err) {
            console.error("redeem error:", err);
            if (!err.response) {
                recordPending("redeem", parsedPassKey);
            } else {
                setError(err.response?.data?.error || "Redeem failed.");
            }
        } finally {
            setLoading(false);
        }
    };

    if (!vendor) {
        return (
            <div className="vendor-page">
                <section className="vendor-surface vendor-hero">
                    <div>
                        <span className="vendor-badge">Vendor Operations</span>
                        <h1>Vendor Portal</h1>
                        <p className="vendor-intro-copy">
                            Sign in with your vendor email and password, then unlock the portal with your vendor API key.
                        </p>
                        <ul className="vendor-check-list">
                            <li>Fast traveler QR scanning on mobile or scanner-input desktop.</li>
                            <li>Live verification and auto redemption using BBP pass rules.</li>
                            <li>Offline-safe pending queue for later sync.</li>
                        </ul>
                    </div>

                    <div className="vendor-side-stack">
                        <div className="vendor-surface vendor-auth-card">
                            <div className="vendor-section-head">
                                <div>
                                    <span className="vendor-eyebrow">Secure access</span>
                                    <h2>Login & unlock</h2>
                                </div>
                            </div>

                            <form onSubmit={handleVendorLogin} className="vendor-form-grid">
                                <label className="vendor-field vendor-field-full">
                                    <span>Vendor email</span>
                                    <input type="email" placeholder="Vendor email" value={email} onChange={(e) => setEmail(e.target.value)} />
                                </label>

                                <label className="vendor-field">
                                    <span>Password</span>
                                    <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                                </label>

                                <label className="vendor-field">
                                    <span>Vendor API key</span>
                                    <input type="text" placeholder="Vendor API key" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
                                </label>

                                <div className="vendor-action-row vendor-field-full">
                                    <button type="submit" disabled={loading || scanBusy} className="vendor-primary-btn">
                                        {loading ? "Unlocking…" : "Login & Unlock Portal"}
                                    </button>
                                </div>
                            </form>

                            {error ? <div className="vendor-alert vendor-alert-error">{error}</div> : null}
                            {status ? <div className="vendor-alert vendor-alert-info">{status}</div> : null}
                        </div>

                        <div className="vendor-surface vendor-mini-card">
                            <span className="vendor-eyebrow">What to expect</span>
                            <p>
                                Once unlocked, vendors can scan passes, review redemption history, and confirm which activities are currently active and scanner-ready.
                            </p>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    const canRedeem = verifyResult?.valid === true;

    return (
        <div className="vendor-page">
            <section className="vendor-surface vendor-hero">
                <div>
                    <span className="vendor-badge">{vendor.role || "Vendor"}</span>
                    <h1>{vendor.name} Vendor Portal</h1>
                    <p className="vendor-intro-copy">
                        Scan traveler passes, review every verification attempt, and keep your activity setup aligned with the scanner workflow.
                    </p>
                    <div className="vendor-meta-grid">
                        <div className="vendor-stat-card">
                            <span className="vendor-label">Location</span>
                            <strong>{vendor.location || "No location set"}</strong>
                        </div>
                        <div className="vendor-stat-card">
                            <span className="vendor-label">Pending local scans</span>
                            <strong>{pendingScans.length}</strong>
                        </div>
                    </div>
                </div>

                <div className="vendor-side-stack">
                    <div className="vendor-surface vendor-mini-card">
                        <span className="vendor-label">Session status</span>
                        <strong>Unlocked and ready</strong>
                        <p>{status || "Use the tabs below to scan passes, review redemption history, or inspect activity rules."}</p>
                    </div>
                    <div className="vendor-surface vendor-mini-card">
                        <span className="vendor-label">Sign out</span>
                        <p>End this vendor session on the current device.</p>
                        <button type="button" className="vendor-ghost-btn" onClick={handleVendorLogout}>
                            Sign Out
                        </button>
                    </div>
                </div>
            </section>

            <section className="vendor-surface vendor-tabs-shell">
                <div className="vendor-tabs">
                    <button className={`vendor-tab ${activeTab === "scan" ? "active" : ""}`} onClick={() => setActiveTab("scan")}>
                        Scan Pass
                    </button>
                    <button className={`vendor-tab ${activeTab === "history" ? "active" : ""}`} onClick={() => { setActiveTab("history"); fetchHistory(); }}>
                        Manage Redemptions
                    </button>
                    <button className={`vendor-tab ${activeTab === "activities" ? "active" : ""}`} onClick={() => setActiveTab("activities")}>
                        Manage Activities
                    </button>
                </div>

                <div className="vendor-tab-copy">
                    {activeTab === "scan" ? (
                        <>
                            <h2>Traveler scanning</h2>
                            <p>Fast verify + redeem flow with mobile camera scanning or desktop hardware scanner input.</p>
                        </>
                    ) : null}
                    {activeTab === "history" ? (
                        <>
                            <h2>Redemption oversight</h2>
                            <p>Review valid, failed, and offline-captured events in one clean place.</p>
                        </>
                    ) : null}
                    {activeTab === "activities" ? (
                        <>
                            <h2>Activity readiness</h2>
                            <p>See which products are scanner-ready and how each one behaves at redemption time.</p>
                        </>
                    ) : null}
                </div>
            </section>

            {status ? <div className="vendor-alert vendor-alert-info">{status}</div> : null}
            {error ? <div className="vendor-alert vendor-alert-error">{error}</div> : null}

            {activeTab === "scan" ? (
                <section className="vendor-content-grid">
                    <div className="vendor-surface vendor-panel vendor-panel-main">
                        <div className="vendor-section-head">
                            <div>
                                <span className="vendor-eyebrow">Scan a pass</span>
                                <h2>Verify or redeem instantly</h2>
                            </div>
                        </div>

                        <div className="vendor-form-grid">
                            <label className="vendor-field vendor-field-full">
                                <span>Pass QR payload or UUID</span>
                                <textarea
                                    ref={passInputRef}
                                    rows={3}
                                    placeholder={isNativeApp ? "Tap Open Camera Scanner or paste one BBP QR payload / pass UUID here" : "Scan one pass at a time with your handheld scanner or paste one BBP pass UUID here"}
                                    value={passInput}
                                    onChange={(e) => {
                                        const nextValue = normalizePassTypingInput(e.target.value, passInput);
                                        setPassInput(nextValue);
                                        const typedPass = parsePassInput(nextValue);
                                        if (typedPass && typedPass !== lastProcessedPassKey) {
                                            setLastProcessedPassKey("");
                                        }
                                    }}
                                    onFocus={(e) => {
                                        if (e.currentTarget.value) e.currentTarget.select();
                                    }}
                                    onClick={(e) => {
                                        if (e.currentTarget.value) e.currentTarget.select();
                                    }}
                                    onKeyDown={handlePassFieldKeyDown}
                                />
                            </label>

                            <div className="vendor-field vendor-field-full">
                                <span>Scanner tools</span>
                                <div className="vendor-action-row">
                                    {isNativeApp ? (
                                        <button type="button" className="vendor-secondary-btn" onClick={handleNativeScan} disabled={loading || scanBusy}>
                                            {scanBusy ? "Opening Camera…" : "Open Camera Scanner"}
                                        </button>
                                    ) : (
                                        <button type="button" className="vendor-secondary-btn" onClick={focusPassField}>
                                            Focus Scanner Field
                                        </button>
                                    )}
                                    <div className="vendor-mini-card vendor-subtle-card">
                                        <p>
                                            {isNativeApp
                                                ? "Use the phone camera to scan the Beach Bum Pass QR code. Successful scans can verify and redeem automatically."
                                                : "Desktop scanners usually act like a keyboard. Keep the field focused, scan, and the portal will process the pass automatically."}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <label className="vendor-field vendor-field-full">
                                <span>Activity</span>
                                <select value={selectedActivity} onChange={(e) => setSelectedActivity(e.target.value)}>
                                    <option value="">Select activity</option>
                                    {activities.map((activity) => (
                                        <option key={activity.id} value={activity.id}>{activity.name}</option>
                                    ))}
                                </select>
                            </label>

                            <label className="vendor-field">
                                <span>Customer email (optional)</span>
                                <input type="email" placeholder="Customer email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
                            </label>

                            <label className="vendor-field">
                                <span>Reservation ref (optional)</span>
                                <input type="text" placeholder="Reservation reference" value={reservationRef} onChange={(e) => setReservationRef(e.target.value)} />
                            </label>

                            <label className="vendor-field">
                                <span>Vendor notes (optional)</span>
                                <input type="text" placeholder="Vendor notes" value={vendorNotes} onChange={(e) => setVendorNotes(e.target.value)} />
                            </label>

                            <label className="vendor-field">
                                <span>Scan source</span>
                                <select value={scanSource} onChange={(e) => setScanSource(e.target.value)}>
                                    <option value="manual">Manual entry</option>
                                    {!isNativeApp ? <option value="hardware_scanner">Desktop scanner</option> : null}
                                    {isNativeApp ? <option value="camera">Mobile camera</option> : null}
                                </select>
                            </label>
                        </div>

                        <label className="vendor-checkbox-row">
                            <input type="checkbox" checked={autoRedeem} onChange={(e) => setAutoRedeem(e.target.checked)} />
                            Auto-redeem after a successful verify
                        </label>

                        <div className="vendor-action-row">
                            <button type="button" className="vendor-primary-btn" onClick={() => handleVerify()} disabled={loading || scanBusy}>
                                {loading ? "Checking…" : "Verify Pass"}
                            </button>
                            <button type="button" className="vendor-secondary-btn" onClick={() => handleRedeem()} disabled={loading || scanBusy || !canRedeem}>
                                {loading ? "Logging…" : "Redeem / Log Visit"}
                            </button>
                        </div>
                    </div>

                    <div className="vendor-side-stack">
                        {verifyResult ? (
                            <div className="vendor-surface vendor-panel vendor-result-card" style={{ "--vendor-status": statusColor(verifyResult.status) }}>
                                <div className="vendor-result-head">
                                    <div>
                                        <span className="vendor-eyebrow">Verify result</span>
                                        <h3>{verifyResult.pass_name || "Traveler pass"}</h3>
                                    </div>
                                    <StatusPill status={verifyResult.status} />
                                </div>
                                <div className="vendor-result-grid">
                                    <div className="vendor-mini-card">
                                        <span className="vendor-label">Trip window</span>
                                        <strong>{verifyResult.planned_from || "—"} → {verifyResult.planned_to || "—"}</strong>
                                    </div>
                                    <div className="vendor-mini-card">
                                        <span className="vendor-label">Active window</span>
                                        <strong>{verifyResult.valid_from || "—"} → {verifyResult.valid_to || "—"}</strong>
                                    </div>
                                    <div className="vendor-mini-card">
                                        <span className="vendor-label">Activity usage</span>
                                        <strong>{verifyResult.redemptions_used}/{verifyResult.redemptions_allowed}</strong>
                                    </div>
                                    <div className="vendor-mini-card">
                                        <span className="vendor-label">Scanner status</span>
                                        <strong>{verifyResult.message}</strong>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {redeemResult ? (
                            <div className="vendor-surface vendor-panel vendor-result-card" style={{ "--vendor-status": statusColor(redeemResult.status) }}>
                                <div className="vendor-result-head">
                                    <div>
                                        <span className="vendor-eyebrow">Redeem result</span>
                                        <h3>{redeemResult.pass_name || "Traveler pass"}</h3>
                                    </div>
                                    <StatusPill status={redeemResult.status} />
                                </div>
                                <div className="vendor-result-grid">
                                    <div className="vendor-mini-card">
                                        <span className="vendor-label">Activated now</span>
                                        <strong>{redeemResult.activated_now ? "Yes" : "No"}</strong>
                                    </div>
                                    <div className="vendor-mini-card">
                                        <span className="vendor-label">Trip window</span>
                                        <strong>{redeemResult.planned_from || "—"} → {redeemResult.planned_to || "—"}</strong>
                                    </div>
                                    <div className="vendor-mini-card">
                                        <span className="vendor-label">Active window</span>
                                        <strong>{redeemResult.valid_from || "—"} → {redeemResult.valid_to || "—"}</strong>
                                    </div>
                                    <div className="vendor-mini-card">
                                        <span className="vendor-label">Activity usage</span>
                                        <strong>{redeemResult.redemptions_used}/{redeemResult.redemptions_allowed}</strong>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div className="vendor-surface vendor-panel">
                            <div className="vendor-section-head">
                                <div>
                                    <span className="vendor-eyebrow">Scanner context</span>
                                    <h3>Current activity</h3>
                                </div>
                            </div>
                            {selectedActivityDetails ? (
                                <div className="vendor-info-list">
                                    <div className="vendor-mini-card">
                                        <span className="vendor-label">Selected activity</span>
                                        <strong>{selectedActivityDetails.name}</strong>
                                        <p>{selectedActivityDetails.location || selectedActivityDetails.category || "Scanner-ready activity."}</p>
                                    </div>
                                    <div className="vendor-mini-card">
                                        <span className="vendor-label">Offline safety net</span>
                                        <strong>{pendingScans.length} pending local scans</strong>
                                        <p>If the network drops, this portal stores a pending record for later sync.</p>
                                    </div>
                                </div>
                            ) : (
                                <p className="vendor-empty">Select an activity to see its scanner-ready details.</p>
                            )}
                        </div>
                    </div>
                </section>
            ) : null}

            {activeTab === "history" ? (
                <section className="vendor-content-grid vendor-history-layout">
                    <div className="vendor-side-stack">
                        <div className="vendor-surface vendor-panel">
                            <div className="vendor-section-head">
                                <div>
                                    <span className="vendor-eyebrow">Filter history</span>
                                    <h2>Manage Redemptions</h2>
                                </div>
                                <div className="vendor-export-actions">
                                    <button type="button" className="vendor-refresh-btn" onClick={() => fetchHistory()} disabled={historyLoading || !!exportLoading}>
                                        {historyLoading ? "Loading…" : "Refresh"}
                                    </button>
                                    <button type="button" className="vendor-secondary-btn vendor-export-btn" onClick={() => handleExport("csv")} disabled={historyLoading || !!exportLoading}>
                                        {exportLoading === "csv" ? "Exporting…" : "CSV"}
                                    </button>
                                    <button type="button" className="vendor-secondary-btn vendor-export-btn" onClick={() => handleExport("spreadsheet")} disabled={historyLoading || !!exportLoading}>
                                        {exportLoading === "spreadsheet" ? "Exporting…" : "Spreadsheet"}
                                    </button>
                                </div>
                            </div>

                            <div className="vendor-form-grid vendor-history-filters">
                                <label className="vendor-field">
                                    <span>Event type</span>
                                    <select value={historyFilters.event_type} onChange={(e) => setHistoryFilters((s) => ({ ...s, event_type: e.target.value }))}>
                                        <option value="">All events</option>
                                        <option value="verify">Verify</option>
                                        <option value="redeem">Redeem</option>
                                    </select>
                                </label>

                                <label className="vendor-field">
                                    <span>Result</span>
                                    <select value={historyFilters.result} onChange={(e) => setHistoryFilters((s) => ({ ...s, result: e.target.value }))}>
                                        <option value="">All results</option>
                                        <option value="success">Success</option>
                                        <option value="ready_to_activate">Ready to activate</option>
                                        <option value="duplicate">Already used</option>
                                        <option value="outside_trip_window">Outside trip window</option>
                                        <option value="expired">Expired</option>
                                        <option value="invalid">Invalid</option>
                                    </select>
                                </label>

                                <label className="vendor-field vendor-field-full">
                                    <span>Activity</span>
                                    <select value={historyFilters.activity_id} onChange={(e) => setHistoryFilters((s) => ({ ...s, activity_id: e.target.value }))}>
                                        <option value="">All activities</option>
                                        {activities.map((activity) => (
                                            <option key={activity.id} value={activity.id}>{activity.name}</option>
                                        ))}
                                    </select>
                                </label>

                                <label className="vendor-field">
                                    <span>Rows shown</span>
                                    <select value={historyFilters.limit} onChange={(e) => setHistoryFilters((s) => ({ ...s, limit: Number(e.target.value) }))}>
                                        <option value={100}>Latest 100</option>
                                        <option value={250}>Latest 250</option>
                                        <option value={500}>Latest 500</option>
                                        <option value={1000}>Latest 1,000</option>
                                    </select>
                                </label>
                            </div>
                        </div>

                        <div className="vendor-surface vendor-panel">
                            <div className="vendor-section-head">
                                <div>
                                    <span className="vendor-eyebrow">History stream</span>
                                    <h3>Scan & Redemption History</h3>
                                </div>
                            </div>

                            {mergedHistory.length === 0 ? <p className="vendor-empty">No history yet.</p> : null}

                            <div className="vendor-history-list">
                                {mergedHistory.map((row) => (
                                    <article
                                        key={`${row.pending_local ? "pending" : "server"}-${row.id}-${row.request_id || row.created_at}`}
                                        className="vendor-history-card"
                                        style={{ "--vendor-status": statusColor(row.status || row.result) }}
                                    >
                                        <div className="vendor-history-top">
                                            <div>
                                                <strong>{row.activity?.name || "Unknown activity"}</strong>
                                                <span>{row.event_type}</span>
                                            </div>
                                            <div className="vendor-history-meta">{formatDateTime(row.created_at)}</div>
                                        </div>

                                        <div className="vendor-history-grid vendor-history-grid-tight">
                                            <div>
                                                <span className="vendor-label">Status</span>
                                                <div className="vendor-chip-row">
                                                    <StatusPill status={row.status || row.result} />
                                                    {row.pending_local ? <span className="vendor-chip vendor-chip-muted">Pending local sync</span> : null}
                                                </div>
                                            </div>
                                            <div>
                                                <span className="vendor-label">Pass</span>
                                                <strong>{row.pass?.id || row.snapshot?.pass_id || "Unknown"}</strong>
                                            </div>
                                            <div>
                                                <span className="vendor-label">Pass type</span>
                                                <strong>{row.pass?.pass_type || row.snapshot?.pass_name || "—"}</strong>
                                            </div>
                                            <div>
                                                <span className="vendor-label">Holder</span>
                                                <strong>{row.pass?.holder_name || row.pass?.holder_type || "—"}</strong>
                                            </div>
                                            <div>
                                                <span className="vendor-label">Traveler</span>
                                                <strong>{row.user?.email || row.email || row.snapshot?.email || "—"}</strong>
                                            </div>
                                            <div>
                                                <span className="vendor-label">Trip window</span>
                                                <strong>{row.pass?.planned_start_date || row.snapshot?.planned_from || "—"} → {row.pass?.planned_end_date || row.snapshot?.planned_to || "—"}</strong>
                                            </div>
                                            <div>
                                                <span className="vendor-label">Active window</span>
                                                <strong>{row.pass?.start_date || row.snapshot?.valid_from || "—"} → {row.pass?.end_date || row.snapshot?.valid_to || "—"}</strong>
                                            </div>
                                            <div>
                                                <span className="vendor-label">Usage</span>
                                                <strong>{row.snapshot?.redemptions_used ?? "—"}/{row.snapshot?.redemptions_allowed ?? row.activity?.max_redemptions_per_pass ?? "—"}</strong>
                                            </div>
                                        </div>

                                        <p className="vendor-history-message">{row.message || "No extra message."}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="vendor-side-stack">
                        <div className="vendor-surface vendor-panel">
                            <div className="vendor-section-head">
                                <div>
                                    <span className="vendor-eyebrow">At a glance</span>
                                    <h3>History summary</h3>
                                </div>
                            </div>
                            <div className="vendor-result-grid vendor-summary-grid">
                                <div className="vendor-mini-card">
                                    <span className="vendor-label">Total scans</span>
                                    <strong>{historySummary.total}</strong>
                                </div>
                                <div className="vendor-mini-card vendor-good-card">
                                    <span className="vendor-label">Good scans</span>
                                    <strong>{historySummary.good}</strong>
                                </div>
                                <div className="vendor-mini-card vendor-bad-card">
                                    <span className="vendor-label">Bad scans</span>
                                    <strong>{historySummary.bad}</strong>
                                </div>
                                <div className="vendor-mini-card">
                                    <span className="vendor-label">Redeems</span>
                                    <strong>{historySummary.redeem}</strong>
                                </div>
                                <div className="vendor-mini-card">
                                    <span className="vendor-label">Duplicates</span>
                                    <strong>{historySummary.duplicate}</strong>
                                </div>
                                <div className="vendor-mini-card">
                                    <span className="vendor-label">Pending local</span>
                                    <strong>{historySummary.pending}</strong>
                                </div>
                            </div>
                        </div>

                        <div className="vendor-surface vendor-panel vendor-export-panel">
                            <div className="vendor-section-head">
                                <div>
                                    <span className="vendor-eyebrow">Vendor report</span>
                                    <h3>Export scan history</h3>
                                </div>
                            </div>
                            <p className="vendor-message">
                                Download the current scan report with pass, traveler, activity, trip-window, and redemption details.
                            </p>
                            <div className="vendor-export-actions vendor-export-actions-stacked">
                                <button type="button" className="vendor-secondary-btn" onClick={() => handleExport("csv")} disabled={historyLoading || !!exportLoading}>
                                    {exportLoading === "csv" ? "Exporting…" : "Download CSV"}
                                </button>
                                <button type="button" className="vendor-secondary-btn" onClick={() => handleExport("spreadsheet")} disabled={historyLoading || !!exportLoading}>
                                    {exportLoading === "spreadsheet" ? "Exporting…" : "Download Spreadsheet"}
                                </button>
                            </div>
                            <p className="vendor-export-note">Exports use the active filters and pull up to {EXPORT_LIMIT.toLocaleString()} server records.</p>
                        </div>

                        <div className="vendor-surface vendor-panel">
                            <div className="vendor-section-head">
                                <div>
                                    <span className="vendor-eyebrow">Support notes</span>
                                    <h3>What this tab shows</h3>
                                </div>
                            </div>
                            <ul className="vendor-check-list">
                                <li>Every verify and redeem event captured by this vendor session.</li>
                                <li>Pending local scans stored while offline.</li>
                                <li>Traveler, pass, and trip-window context for quick support follow-up.</li>
                            </ul>
                        </div>
                    </div>
                </section>
            ) : null}

            {activeTab === "activities" ? (
                <section className="vendor-content-grid vendor-history-layout">
                    <div className="vendor-side-stack">
                        <div className="vendor-surface vendor-panel">
                            <div className="vendor-section-head">
                                <div>
                                    <span className="vendor-eyebrow">Scanner-ready catalog</span>
                                    <h2>Manage Activities</h2>
                                </div>
                            </div>
                            <p className="vendor-message">
                                This tab is ready for the later image-upload and content-management iteration. For now, it shows the redemption behavior the portal and standalone scanner app rely on.
                            </p>
                            <div className="vendor-activity-grid vendor-activity-grid-rich">
                                {activities.map((activity) => (
                                    <article key={activity.id} className="vendor-activity-card">
                                        <div className="vendor-activity-top">
                                            <div>
                                                <h3>{activity.name}</h3>
                                                <div className="vendor-chip-row">
                                                    {activity.category ? <span className="vendor-chip">{activity.category}</span> : null}
                                                    {activity.location ? <span className="vendor-chip vendor-chip-muted">{activity.location}</span> : null}
                                                </div>
                                            </div>
                                            <StatusPill status={activity.active === false ? "INACTIVE" : "ACTIVE"} />
                                        </div>

                                        <div className="vendor-activity-meta">
                                            <div>
                                                <span className="vendor-label">Redemption mode</span>
                                                <strong>{activity.redemption_mode || "once_per_pass"}</strong>
                                            </div>
                                            <div>
                                                <span className="vendor-label">Max per pass</span>
                                                <strong>{activity.max_redemptions_per_pass || "1"}</strong>
                                            </div>
                                            <div>
                                                <span className="vendor-label">Reservation required</span>
                                                <strong>{activity.requires_reservation ? "Yes" : "No"}</strong>
                                            </div>
                                            <div>
                                                <span className="vendor-label">Scanner enabled</span>
                                                <strong>{activity.scanner_enabled === false ? "No" : "Yes"}</strong>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="vendor-side-stack">
                        <div className="vendor-surface vendor-panel">
                            <div className="vendor-section-head">
                                <div>
                                    <span className="vendor-eyebrow">Scanner notes</span>
                                    <h3>How activities behave</h3>
                                </div>
                            </div>
                            <div className="vendor-info-list">
                                <div className="vendor-info-item">
                                    <div>•</div>
                                    <p>Each activity tells the scanner how many times a pass can redeem it.</p>
                                </div>
                                <div className="vendor-info-item">
                                    <div>•</div>
                                    <p>Reservation-required products can capture reservation references during redemption.</p>
                                </div>
                                <div className="vendor-info-item">
                                    <div>•</div>
                                    <p>Scanner-enabled status helps separate live products from placeholders or paused offers.</p>
                                </div>
                            </div>
                        </div>

                        <div className="vendor-surface vendor-panel">
                            <div className="vendor-section-head">
                                <div>
                                    <span className="vendor-eyebrow">Summary</span>
                                    <h3>Catalog health</h3>
                                </div>
                            </div>
                            <div className="vendor-result-grid">
                                <div className="vendor-mini-card">
                                    <span className="vendor-label">Total activities</span>
                                    <strong>{activities.length}</strong>
                                </div>
                                <div className="vendor-mini-card">
                                    <span className="vendor-label">Reservation required</span>
                                    <strong>{activities.filter((a) => a.requires_reservation).length}</strong>
                                </div>
                                <div className="vendor-mini-card">
                                    <span className="vendor-label">Scanner enabled</span>
                                    <strong>{activities.filter((a) => a.scanner_enabled !== false).length}</strong>
                                </div>
                                <div className="vendor-mini-card">
                                    <span className="vendor-label">One-time passes</span>
                                    <strong>{activities.filter((a) => !a.redemption_mode || a.redemption_mode === "once_per_pass").length}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            ) : null}
        </div>
    );
}
